import React from 'react';
import { Shield, Lock, Eye, FileText, Server, Cookie, Share2, UserCheck, Globe, RefreshCcw } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="bg-white min-h-screen pb-24">
      {/* Header */}
      <section className="bg-black py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-600/20 rounded-full blur-[100px] -mr-20 -mt-20 pointer-events-none"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-gray-900 border border-gray-800 mb-6">
                <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">
                    Legal & Compliance
                </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-6 tracking-tight">
                Privacy <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Policy</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                Effective Date: January 1, 2025
            </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100">
            
            <div className="prose prose-lg max-w-none text-gray-600">
                <p className="lead text-xl text-gray-900 font-medium mb-10 border-b border-gray-100 pb-8">
                    At BuildMySupplement ("we," "our," or "us"), we are committed to protecting the privacy and security of your personal and business information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our manufacturing services, or engage with our team.
                </p>

                {/* Section 1 */}
                <div className="flex items-start gap-5 mb-10">
                    <div className="p-3 bg-orange-50 rounded-xl text-orange-600 flex-shrink-0 mt-1">
                        <Shield size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">1. Information We Collect</h3>
                        <p className="text-sm md:text-base mb-3">
                            We collect specific types of data to provide our manufacturing and consultation services effectively. This may include:
                        </p>
                        <ul className="list-disc pl-5 mt-2 space-y-2 text-sm md:text-base marker:text-orange-500">
                            <li><strong>Personal Identity Information:</strong> Name, email address, phone number, and job title when you request a quote or book a consultation.</li>
                            <li><strong>Business Data:</strong> Company name, registration details (GSTIN, CIN), FSSAI license numbers, and billing address.</li>
                            <li><strong>Proprietary Product Data:</strong> Custom formulations, ingredient lists, flavor profiles, packaging designs, and label artwork.</li>
                            <li><strong>Technical Data:</strong> IP address, browser type, operating system, and usage patterns when you navigate our website (collected via cookies).</li>
                        </ul>
                    </div>
                </div>

                {/* Section 2 */}
                <div className="flex items-start gap-5 mb-10">
                    <div className="p-3 bg-orange-50 rounded-xl text-orange-600 flex-shrink-0 mt-1">
                        <Eye size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">2. How We Use Your Information</h3>
                        <p className="text-sm md:text-base mb-3">
                           We use the collected information for specific business purposes:
                        </p>
                        <ul className="list-disc pl-5 mt-2 space-y-2 text-sm md:text-base marker:text-orange-500">
                            <li><strong>Service Delivery:</strong> To provide accurate manufacturing quotes, assess feasibility, and process production orders.</li>
                            <li><strong>Regulatory Compliance:</strong> To apply for necessary approvals (FSSAI product approval, label claims verification) on your behalf.</li>
                            <li><strong>Communication:</strong> To send order updates, invoices, and respond to your inquiries or support requests.</li>
                            <li><strong>Improvement:</strong> To analyze website traffic and improve our digital user experience.</li>
                            <li><strong>Marketing:</strong> With your consent, we may send industry insights or newsletters (you can opt-out at any time).</li>
                        </ul>
                    </div>
                </div>

                {/* Section 3 */}
                <div className="flex items-start gap-5 mb-10">
                    <div className="p-3 bg-orange-50 rounded-xl text-orange-600 flex-shrink-0 mt-1">
                        <Lock size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">3. Intellectual Property (IP) Protection</h3>
                        <p className="text-sm md:text-base mb-3">
                            We recognize that your custom formulations are your most valuable asset. We adhere to strict protocols to ensure your IP remains yours.
                        </p>
                        <ul className="list-disc pl-5 mt-2 space-y-2 text-sm md:text-base marker:text-orange-500">
                            <li><strong>Non-Disclosure Agreements (NDAs):</strong> We are willing to sign a mutual NDA before discussing detailed custom formulations or trade secrets.</li>
                            <li><strong>Ownership Rights:</strong> Unless otherwise specified in a separate contract, you retain full ownership of the custom brand assets and specific custom formulations developed exclusively for you.</li>
                            <li><strong>Data Segregation:</strong> Client data is siloed. Your formulation details are strictly accessible only to the specific R&D and production personnel working on your project.</li>
                        </ul>
                    </div>
                </div>

                {/* Section 4 */}
                <div className="flex items-start gap-5 mb-10">
                    <div className="p-3 bg-orange-50 rounded-xl text-orange-600 flex-shrink-0 mt-1">
                        <Cookie size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">4. Cookies and Tracking Technologies</h3>
                        <p className="text-sm md:text-base">
                            We use cookies and similar tracking technologies to track the activity on our web service and hold certain information.
                        </p>
                        <ul className="list-disc pl-5 mt-2 space-y-2 text-sm md:text-base marker:text-orange-500">
                            <li><strong>Essential Cookies:</strong> Necessary for the website to function properly.</li>
                            <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with the website (e.g., Google Analytics).</li>
                            <li><strong>Functionality Cookies:</strong> Allow us to remember choices you make when you use our website.</li>
                        </ul>
                        <p className="text-sm md:text-base mt-2">
                            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                        </p>
                    </div>
                </div>

                {/* Section 5 */}
                <div className="flex items-start gap-5 mb-10">
                    <div className="p-3 bg-orange-50 rounded-xl text-orange-600 flex-shrink-0 mt-1">
                        <Share2 size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">5. Sharing of Information</h3>
                        <p className="text-sm md:text-base">
                            We do not sell, trade, or otherwise transfer your Personally Identifiable Information to outside parties for marketing purposes. We may share data in the following circumstances:
                        </p>
                        <ul className="list-disc pl-5 mt-2 space-y-2 text-sm md:text-base marker:text-orange-500">
                            <li><strong>Trusted Third Parties:</strong> Logistics partners for shipping, lab partners for testing, or payment gateways for processing transactions, provided they agree to keep this information confidential.</li>
                            <li><strong>Legal Obligations:</strong> When required to comply with the law, enforce our site policies, or protect ours or others' rights, property, or safety.</li>
                            <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
                        </ul>
                    </div>
                </div>

                {/* Section 6 */}
                <div className="flex items-start gap-5 mb-10">
                    <div className="p-3 bg-orange-50 rounded-xl text-orange-600 flex-shrink-0 mt-1">
                        <Server size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">6. Data Security & Retention</h3>
                        <p className="text-sm md:text-base">
                            We implement a variety of security measures to maintain the safety of your personal information. Your personal information is contained behind secured networks and is only accessible by a limited number of persons who have special access rights.
                        </p>
                        <p className="text-sm md:text-base mt-2">
                            We retain your personal and business data only for as long as is necessary for the purposes set out in this Privacy Policy, or as required by law (e.g., for tax and accounting purposes).
                        </p>
                    </div>
                </div>

                 {/* Section 7 */}
                 <div className="flex items-start gap-5 mb-10">
                    <div className="p-3 bg-orange-50 rounded-xl text-orange-600 flex-shrink-0 mt-1">
                        <UserCheck size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">7. Your Data Rights</h3>
                        <p className="text-sm md:text-base">
                            Depending on your location, you may have the following rights regarding your personal data:
                        </p>
                        <ul className="list-disc pl-5 mt-2 space-y-2 text-sm md:text-base marker:text-orange-500">
                            <li>The right to access, update, or delete the information we have on you.</li>
                            <li>The right of rectification (to correct inaccurate information).</li>
                            <li>The right to object to our processing of your personal data.</li>
                            <li>The right to request that we restrict the processing of your personal information.</li>
                            <li>The right to withdraw consent at any time where we relied on your consent to process your personal information.</li>
                        </ul>
                    </div>
                </div>

                 {/* Section 8 */}
                 <div className="flex items-start gap-5 mb-10">
                    <div className="p-3 bg-orange-50 rounded-xl text-orange-600 flex-shrink-0 mt-1">
                        <Globe size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">8. International Data Transfers</h3>
                        <p className="text-sm md:text-base">
                            Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ than those from your jurisdiction.
                        </p>
                        <p className="text-sm md:text-base mt-2">
                            If you are located outside India and choose to provide information to us, please note that we transfer the data, including Personal Data, to India and process it there. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
                        </p>
                    </div>
                </div>

                 {/* Section 9 */}
                 <div className="flex items-start gap-5 mb-10">
                    <div className="p-3 bg-orange-50 rounded-xl text-orange-600 flex-shrink-0 mt-1">
                        <RefreshCcw size={24} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">9. Changes to This Privacy Policy</h3>
                        <p className="text-sm md:text-base">
                            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes.
                        </p>
                    </div>
                </div>

                <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 mt-12 text-center">
                    <h4 className="font-bold text-gray-900 mb-3 text-lg">Have Questions?</h4>
                    <p className="text-sm text-gray-600 mb-4">
                        If you have questions about this policy or wish to exercise your data rights, please contact our compliance officer.
                    </p>
                    <a href="mailto:privacy@buildmysupplement.com" className="inline-flex items-center justify-center px-6 py-3 border border-gray-200 bg-white rounded-lg text-orange-600 font-bold hover:bg-orange-50 hover:border-orange-200 transition-all shadow-sm">
                        privacy@buildmysupplement.com
                    </a>
                </div>

            </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;